import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  IconButton,
  useMediaQuery,
  Collapse,
  CircularProgress
} from '@mui/material';
import { ChevronRight, ChevronLeft, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useSelector } from 'react-redux';

const LpAnalysisSecondaryMenuFlat = ({ categories, onFieldSelect, onMinimizeToggle, minimized }) => {
  // console.log('LpAnalysisSecondaryMenuFlat render - minimized:', minimized);
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const drawerOpen = useSelector((state) => state.menu.drawerOpen);
  const [openDropdown, setOpenDropdown] = useState(null);



  const handleDropdownToggle = (categoryName) => {
    if (openDropdown === categoryName) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(categoryName);
    }
  };

  // Get the main menu width based on drawer state
  const getMainMenuWidth = () => (drawerOpen ? 195 : 64);

  const handleMinimizeToggle = () => {
    if (onMinimizeToggle) {
      onMinimizeToggle(!minimized);
    }
  };


  if (minimized) {
    return (
      <IconButton
        size="small"
        color="default"
        aria-label="expand menu"
        onClick={handleMinimizeToggle}
        sx={{
          position: 'fixed',
          top: isMobile ? 80 : 11,
          left: isMobile ? 0 : (theme.direction === 'rtl' ? 'auto' : getMainMenuWidth()),
          right: isMobile ? 'auto' : (theme.direction === 'rtl' ? getMainMenuWidth() : 'auto'),
          zIndex: 2500,
          pointerEvents: 'auto',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          bgcolor: 'background.paper',
          color: 'text.primary',
          transition: 'left 0.3s ease',
          '&:hover': {
            bgcolor: 'background.paper',
            opacity: 0.9
          }
        }}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: isMobile ? 0 : (theme.direction === 'rtl' ? 'auto' : getMainMenuWidth()),
        right: isMobile ? 0 : (theme.direction === 'rtl' ? getMainMenuWidth() : 'auto'),
        width: isMobile ? '100%' : 220,
        height: '100vh',
        backgroundColor: theme.palette.background.paper,
        borderRight: '1px solid',
        borderLeft: theme.direction === 'rtl' ? '1px solid' : 'none',
        borderColor: 'divider',
        zIndex: isMobile ? 1301 : 999,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'left 0.3s ease, right 0.3s ease, width 0.3s ease',
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        '&::-webkit-scrollbar': {
          width: '4px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.primary.light,
          borderRadius: '4px'
        }
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          color: 'text.primary',
          py: 1,
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Website Analysis
        </Typography>
        <IconButton size="small" onClick={handleMinimizeToggle} sx={{ color: 'text.secondary' }}>
          <ChevronLeft fontSize="small" />
        </IconButton>
      </Box>

      <List component="nav" dense disablePadding>
        {Object.entries(categories).map(([categoryName, categoryData]) => {
          // Special handling ONLY for the 'Competitors' category
          if (categoryName === 'Competitors') {
            const hasFields = categoryData.fields && categoryData.fields.length > 0;
            const isDropdownOpen = openDropdown === categoryName;

            return (
              <React.Fragment key={categoryName}>
                <ListItemButton
                  onClick={() => (hasFields ? handleDropdownToggle(categoryName) : onFieldSelect({ category: categoryName }))}
                  sx={{ bgcolor: 'background.neutral', py: 1, minHeight: 36 }}
                >
                  <ListItemText
                    primary={
                      <Typography 
                        variant="subtitle2" 
                        sx={{ fontWeight: 700, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onFieldSelect({ category: categoryName, field: { name: categoryName } });
                        }}
                      >
                        {categoryName}
                      </Typography>
                    }
                  />
                  {categoryData.loading ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : hasFields ? (
                    isDropdownOpen ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )
                  ) : null}
                </ListItemButton>
                {hasFields && (
                  <Collapse in={isDropdownOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding dense>
                      {categoryData.fields.map((field) => (
                        <ListItemButton key={field.id} onClick={() => onFieldSelect({ category: categoryName, field })} sx={{ pl: 4, py: 0.5 }}>
                          <ListItemText
                            primary={<Typography variant="body2">{field.name}</Typography>}
                            primaryTypographyProps={{
                              style: {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
                <Divider />
              </React.Fragment>
            );
          }

          // Default rendering for all other categories
          return (
            <React.Fragment key={categoryName}>
              <ListItemButton
                onClick={() => onFieldSelect({ category: categoryName, field: { name: categoryName } })}
                sx={{
                  bgcolor: 'background.neutral',
                  py: 0.5,
                  minHeight: 36,
                  opacity: 1,
                  cursor: 'pointer'
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {categoryName}
                    </Typography>
                  }
                />
              </ListItemButton>
              <Divider />
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

LpAnalysisSecondaryMenuFlat.propTypes = {
  categories: PropTypes.object.isRequired,
  onFieldSelect: PropTypes.func.isRequired,
  onMinimizeToggle: PropTypes.func,
  minimized: PropTypes.bool
};

export default LpAnalysisSecondaryMenuFlat; 